export interface BrowseSeoBreadcrumb {
  name: string;
  url: string;
}

export type BrowseSeoKind = 'landing' | 'picker' | 'listings';

export interface BrowseSeoInput {
  /** Path segments (no leading empty), e.g. from router URL without query. */
  urlSegments: string[];
  /** Override for the leaf label (already humanized). */
  leafLabel?: string;
  /** Published listing count for this browse scope, when known. */
  listingCount?: number | null;
  /** Child category rows on a picker/landing page. */
  childCategoryCount?: number | null;
  /** Sum of item counts reported on child category rows. */
  childListingTotal?: number | null;
  kind?: BrowseSeoKind;
  pageUrl?: string;
}

export interface BrowseSeoMeta {
  title: string;
  description: string;
  pageUrl?: string;
  breadcrumbs: BrowseSeoBreadcrumb[];
}

/** Path-only URL segments (strips query/hash). */
export function browsePathSegments(routerUrl: string): string[] {
  return (routerUrl || '').split(/[?#]/)[0].split('/').filter(Boolean);
}

/** Taxonomy (+ optional profile) segments used for browse SEO crumbs — drops trailing `all`. */
export function browseSeoSegments(urlSegments: string[]): string[] {
  return (urlSegments || []).filter(segment => !!segment && segment !== 'all');
}

export function formatBrowseSegmentLabel(segment: string): string {
  if (!segment) {
    return '';
  }
  return segment
    .replace(/-/g, ' ')
    .replace(/\band\b/gi, '&')
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Sum item counts from category picker API rows (`item[5]` / `item[4]`). */
export function sumCategoryItemCounts(items: any[][] | null | undefined): number {
  let total = 0;
  for (const item of items || []) {
    const raw = item?.[5] ?? item?.[4];
    if (raw == null || raw === '') {
      continue;
    }
    const n = Number(raw);
    if (!Number.isNaN(n)) {
      total += n;
    }
  }
  return total;
}

export function buildBrowseSeoMeta(input: BrowseSeoInput): BrowseSeoMeta {
  const segments = browseSeoSegments(input.urlSegments);
  const crumbs: BrowseSeoBreadcrumb[] = [{name: 'Home', url: '/home'}];
  let path = '';
  for (const segment of segments) {
    path += `/${segment}`;
    crumbs.push({
      name: formatBrowseSegmentLabel(segment),
      url: path
    });
  }

  const leafFromPath = segments.length
    ? formatBrowseSegmentLabel(segments[segments.length - 1])
    : 'Browse';
  const label = (input.leafLabel || '').trim() || leafFromPath;
  const kind = input.kind || (segments.length <= 1 ? 'landing' : 'picker');
  const description = buildDescription(label, kind, input);

  return {
    title: `${label} | Marketplace`,
    description,
    pageUrl: input.pageUrl,
    breadcrumbs: crumbs
  };
}

function buildDescription(label: string, kind: BrowseSeoKind, input: BrowseSeoInput): string {
  let base: string;
  if (kind === 'landing') {
    base = `Explore ${label} categories and listings on our marketplace.`;
  } else if (kind === 'listings') {
    base = `Browse ${label} listings — compare prices, filters, and seller details.`;
  } else {
    base = `Explore ${label} subcategories and listings on our marketplace.`;
  }

  const extras: string[] = [];
  const listingCount = normalizeCount(input.listingCount);
  const childListingTotal = normalizeCount(input.childListingTotal);
  const childCategoryCount = normalizeCount(input.childCategoryCount);

  if (kind === 'listings' && listingCount != null) {
    extras.push(listingCount === 1 ? '1 listing available.' : `${listingCount} listings available.`);
  } else if (childListingTotal != null && childListingTotal > 0) {
    extras.push(childListingTotal === 1 ? '1 listing in this category.' : `${childListingTotal} listings in this category.`);
  } else if (listingCount != null && listingCount > 0) {
    extras.push(listingCount === 1 ? '1 listing available.' : `${listingCount} listings available.`);
  }

  if (kind !== 'listings' && childCategoryCount != null && childCategoryCount > 0) {
    extras.push(childCategoryCount === 1
      ? '1 subcategory to browse.'
      : `${childCategoryCount} subcategories to browse.`);
  }

  if (!extras.length) {
    return base;
  }
  return `${base} ${extras.join(' ')}`;
}

function normalizeCount(value: number | null | undefined): number | null {
  if (value == null) {
    return null;
  }
  const n = Number(value);
  if (Number.isNaN(n) || n < 0) {
    return null;
  }
  return Math.floor(n);
}
